/**
 * V2 Import from Google Maps — /lists/import (2026-08-03).
 *
 * Walks the owner from a Google Takeout "Saved" CSV to a Yoink list:
 * intro (how to get the file) → file pick → paced matching with live
 * progress → review (confident matches pre-checked) → import → done.
 * Google's per-place notes come along as entry notes.
 *
 * Big files (2026-08-04 on-device: a 1,080-row "Want to go") match in
 * user-driven batches of IMPORT_BATCH with a "Match the next N" button,
 * and the review sorts attention-first: misses and places you ALREADY
 * have (journal or any list — detected by google_place_id, DB-only)
 * above the ready matches, freshly added rows at the bottom.
 *
 * iPad-first: the file input opens the Files app, every failure is
 * on-page text, and both long phases stream progress into the DOM
 * without re-rendering (a re-render would drop scroll position and
 * checkbox state mid-walk).
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { createLocationFromPlace } from '../log/places.js';
import { takeoutRows, resolveTakeoutRows, IMPORT_BATCH, PACING, sleep } from './takeout.js';
import { addToList, createList, ensureWantToGo, fetchMyLists, WANT_TO_GO } from './lists.js';

const ROW_STATUS = {
  matched: (row) => escapeHtml(row.place.secondaryText || ''),
  uncertain: (row) => `Best guess — your list says “${escapeHtml(row.title)}”`,
  closed: () => '<span class="list-entry-closed">Permanently closed</span> — tick it only if you want the memory',
  unmatched: () => 'Couldn’t match this one',
  rejected: () => '<span class="list-entry-closed">Google calls this an area</span>, not a place — it can’t be added; find it with the list page’s “Add a place”',
  already: () => 'Already in your journal or lists — tick it to add it here too',
  added: () => 'Added ✓',
};

// Attention-first review order (2026-08-04 on-device feedback): the rows
// needing a look — misses and things you already have — surface above the
// bulk of ready-to-import matches; freshly added rows sink to the bottom.
const STATUS_RANK = { unmatched: 0, rejected: 0, already: 1, closed: 2, uncertain: 3, matched: 4, added: 5 };

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  let fileName = '';
  let allRows = []; // every parsed row of the file
  let matchedUpTo = 0; // rows resolved so far — batches advance this
  let resolved = []; // rows + { status, place? }
  let checked = new Set(); // indexes into resolved
  let dest = 'new'; // 'new' | 'wtg' | 'existing'
  let myCustomLists = []; // merge targets for 'existing' (2026-08-03 ask)
  let existingListId = null;
  let stopAsked = false;
  let done = null; // { added, skipped, duplicate, rejected[], slug, title }

  // A 1,080-row import is a multi-sitting job (2026-08-04 on-device:
  // "had to start over a lot if I left the page") — the whole session
  // (file rows, match results, ticks, progress) persists per device and
  // offers to resume. Google is never asked twice for a matched row.
  const SESSION_KEY = 'yoink_import_session';

  function saveSession() {
    if (!allRows.length) return;
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        fileName, allRows, resolved, matchedUpTo, checked: [...checked], dest, existingListId,
      }));
    } catch { /* quota/private mode — resuming just won't survive a leave */ }
  }

  function clearSession() {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch { /* private mode */ }
  }

  function restoreSession() {
    try {
      const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      if (!s || !Array.isArray(s.allRows) || !s.allRows.length) return false;
      fileName = s.fileName || '';
      allRows = s.allRows;
      resolved = Array.isArray(s.resolved) ? s.resolved : [];
      matchedUpTo = Number(s.matchedUpTo) || 0;
      checked = new Set(Array.isArray(s.checked) ? s.checked : []);
      dest = s.dest || 'new';
      existingListId = s.existingListId || null;
      return true;
    } catch {
      return false;
    }
  }

  function renderResume() {
    const addedCount = resolved.filter((r) => r.status === 'added').length;
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Pick up where you left off">
        <h2 class="v2-page-title">Pick up where you left off?</h2>
        <p>“${escapeHtml(fileName || 'Your import')}” — ${matchedUpTo} of ${allRows.length}
          matched${addedCount ? `, ${addedCount} added` : ''}. Your matches are saved;
          continuing never looks anything up twice.</p>
        <button type="button" class="v2-btn-leather" data-import-resume>Continue this import</button>
        <button type="button" class="v2-btn-more" data-import-startover>Start over with a new file</button>
      </section>`;
  }

  const back = `<nav class="v2-back" aria-label="Back">
    <a href="${basePath}/lists" data-back-lists>← My lists</a></nav>`;

  function defaultTitle() {
    return fileName.replace(/\.csv$/i, '').trim() || 'Imported places';
  }

  function renderIntro(error = '') {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Import from Google Maps">
        <h2 class="v2-page-title">Import from Google Maps</h2>
        <p>Bring a saved list — Want to go, Favorites, or any custom list — into your journal.</p>
        <ol class="import-steps">
          <li>Open <a class="import-takeout-link" href="https://takeout.google.com" target="_blank"
            rel="noopener"><strong>takeout.google.com</strong></a> and sign in with your Google account.</li>
          <li>Tap “Deselect all”, then check only <strong>Saved</strong>. (The export includes
            all your lists, but each arrives as its own file — you choose which ones to import.)</li>
          <li>Tap “Next step”, then “Create export”. Google prepares a small download — usually within a few minutes.</li>
          <li>Download it and unzip it. Inside <strong>Takeout/Saved</strong> there’s one CSV file per list.</li>
        </ol>
        <details class="import-why">
          <summary>Why so many steps?</summary>
          <p>Google Maps has no export button — Takeout is Google’s official way to take
            your data with you. The good news: you only do this once per import, and your
            places live here after that.</p>
        </details>
        <label class="v2-btn-leather import-file-btn">Choose a CSV file
          <input type="file" accept=".csv,text/csv" data-import-file hidden>
        </label>
        <p class="log-hint">Pick one list’s file — like “Want to go.csv”. You can come back for the others.</p>
        <p class="log-hint import-error" data-import-error aria-live="polite">${escapeHtml(error)}</p>
      </section>`;
  }

  /** Big files stop here first: the batches are a CHOICE, not a wall. */
  function renderBatchPrompt() {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="A big list">
        <h2 class="v2-page-title">${allRows.length} places in this file</h2>
        <p>That’s a big one. Google limits how fast places can be looked up, so
          this works ${IMPORT_BATCH} at a time: match a batch (a couple of
          minutes), review and import it, then come back for the next
          ${IMPORT_BATCH} whenever you like — the file stays loaded.</p>
        <button type="button" class="v2-btn-leather" data-import-more>Match the first ${IMPORT_BATCH}</button>
      </section>`;
  }

  function renderMatching(start, count) {
    const whole = allRows.length;
    const batchLabel = whole > count ? `places ${start + 1}–${start + count} of ${whole}` : 'your places';
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Matching your places">
        <h2 class="v2-page-title">Matching ${batchLabel}…</h2>
        <p data-match-line aria-live="polite">1 of ${count} in this batch…</p>
        <progress class="import-progress" data-match-bar max="${count}" value="0"></progress>
        <p class="log-hint">Google limits how fast places can be looked up — a batch of
          ${count} takes a couple of minutes. Keep this page open.</p>
        <button type="button" class="v2-btn-more" data-import-stop>Stop and review what’s matched</button>
      </section>`;
  }

  function reviewRow(row, i) {
    const isChecked = checked.has(i);
    const dead = !row.place || row.status === 'added' || row.status === 'rejected';
    const name = row.place ? row.place.mainText : row.title || 'Untitled place';
    const sub = (ROW_STATUS[row.status] || (() => ''))(row);
    return `
      <label class="import-row ${dead ? 'import-row-dead' : ''}">
        <input type="checkbox" data-import-check="${i}" ${isChecked ? 'checked' : ''}
          ${dead ? 'disabled' : ''}>
        <span class="import-row-body">
          <strong>${escapeHtml(name)}</strong>
          ${sub ? `<span class="import-row-sub">${sub}</span>` : ''}
          ${row.note ? `<span class="import-row-sub">“${escapeHtml(row.note)}” — comes along as a note</span>` : ''}
        </span>
      </label>`;
  }

  /** Rows a tick can still act on — the inverse of reviewRow's `dead`. */
  function tickable(row) {
    return !!row.place && row.status !== 'added';
  }

  /**
   * Bulk ticks (2026-08-25 on-device: a 407-row In-N-Out import came back
   * with 97 rows "already in your journal or lists", every one of them
   * something the owner DID want in the new list). Those rows have always
   * been importable — runImport() only asks for checked && row.place — but
   * the only way to say yes to them was 97 taps.
   *
   * Ticking them stays deliberate rather than becoming the default: the
   * 2026-08-04 rule that an already-saved row arrives UNchecked is what
   * makes it visible in the first place. This just makes answering "yes,
   * all of them" one tap instead of ninety-seven.
   */
  function bulkBarHtml() {
    let canTick = 0;
    let unticked = 0;
    let untickedAlready = 0;
    resolved.forEach((row, i) => {
      if (!tickable(row)) return;
      canTick++;
      if (checked.has(i)) return;
      unticked++;
      if (row.status === 'already') untickedAlready++;
    });
    if (!canTick) return '';
    const buttons = [
      untickedAlready
        ? `<button type="button" class="v2-chip" data-import-bulk="already">Tick the ${untickedAlready} you already have</button>`
        : '',
      unticked
        ? `<button type="button" class="v2-chip" data-import-bulk="all">Tick all ${canTick}</button>`
        : '',
      checked.size
        ? `<button type="button" class="v2-chip" data-import-bulk="none">Untick all</button>`
        : '',
    ].filter(Boolean).join('');
    // The attribute rides on the real bar too — applyBulk() and the
    // single-tick handler swap this node in place by that selector.
    return buttons ? `<div class="import-bulk" data-import-bulk-bar>${buttons}</div>` : '';
  }

  /**
   * Apply a bulk tick to the checkboxes IN PLACE. A re-render would jump a
   * 400-row review back to the top, which on a touch device loses your
   * place entirely — the same reason the matching phase streams into the
   * DOM instead of re-rendering.
   */
  function applyBulk(mode) {
    resolved.forEach((row, i) => {
      if (!tickable(row)) return;
      if (mode === 'none') checked.delete(i);
      else if (mode === 'all') checked.add(i);
      else if (mode === 'already' && row.status === 'already') checked.add(i);
    });
    for (const box of el.querySelectorAll('[data-import-check]')) {
      box.checked = checked.has(Number(box.dataset.importCheck));
    }
    const go = el.querySelector('[data-import-go]');
    if (go) {
      go.disabled = !checked.size;
      go.textContent = `Add ${checked.size} place${checked.size === 1 ? '' : 's'}`;
    }
    const bar = el.querySelector('[data-import-bulk-bar]');
    if (bar) bar.outerHTML = bulkBarHtml() || '<div data-import-bulk-bar hidden></div>';
    saveSession();
  }

  function renderReview() {
    // Distinct buckets, so no two numbers on this screen can disagree
    // (2026-08-04: "matched" used to lump in already-saved and closed
    // rows, so it never equaled the Add button's count)
    const ready = resolved.filter((r) => r.place && r.status !== 'added' && r.status !== 'already').length;
    const alreadyCount = resolved.filter((r) => r.status === 'already').length;
    const missCount = resolved.filter((r) => !r.place).length;
    const addedCount = resolved.filter((r) => r.status === 'added').length;
    const remaining = allRows.length - matchedUpTo;
    const countsLine = [
      `${ready} matched`,
      alreadyCount ? `${alreadyCount} you already have` : '',
      missCount ? `${missCount} couldn’t match` : '',
      addedCount ? `${addedCount} added ✓` : '',
      remaining ? `${remaining} still to match` : '',
    ].filter(Boolean).join(' · ');
    // Attention-first: sort the VIEW; checkbox indexes still point into
    // resolved, so ticks survive the reorder.
    const order = resolved.map((_, i) => i)
      .sort((a, b) => (STATUS_RANK[resolved[a].status] ?? 4) - (STATUS_RANK[resolved[b].status] ?? 4) || a - b);
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Review your import">
        <h2 class="v2-page-title">Review your import</h2>
        <p>${countsLine}.</p>
        ${remaining ? `<button type="button" class="v2-btn-more" data-import-more>
          Match the next ${Math.min(IMPORT_BATCH, remaining)}</button>` : ''}
        ${bulkBarHtml() || '<div data-import-bulk-bar hidden></div>'}
        <div class="import-rows">${order.map((i) => reviewRow(resolved[i], i)).join('')}</div>
        <fieldset class="import-dest">
          <legend class="mylist-vis-label">Where should they go?</legend>
          <label class="import-dest-choice">
            <input type="radio" name="import-dest" value="wtg" ${dest === 'wtg' ? 'checked' : ''}>
            My Want to go list</label>
          <label class="import-dest-choice">
            <input type="radio" name="import-dest" value="new" ${dest === 'new' ? 'checked' : ''}>
            A new list:
            <input type="text" data-import-title value="${escapeHtml(defaultTitle())}"
              maxlength="80" aria-label="New list name"></label>
          ${myCustomLists.length ? `
          <label class="import-dest-choice">
            <input type="radio" name="import-dest" value="existing" ${dest === 'existing' ? 'checked' : ''}>
            Merge into:
            <select data-import-existing aria-label="Existing list to merge into">
              ${myCustomLists.map((l) => `
              <option value="${escapeHtml(l.id)}" ${l.id === existingListId ? 'selected' : ''}>
                ${escapeHtml(l.title)}</option>`).join('')}
            </select></label>` : ''}
        </fieldset>
        <button type="button" class="v2-btn-leather" data-import-go
          ${checked.size ? '' : 'disabled'}>Add ${checked.size} place${checked.size === 1 ? '' : 's'}</button>
        <p class="log-hint">Unmatched places are easiest to add afterwards with the list page’s
          “Add a place” search.</p>
      </section>`;
  }

  function renderImporting(total) {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Adding your places">
        <h2 class="v2-page-title">Adding your places…</h2>
        <p data-import-line aria-live="polite">Adding 1 of ${total}…</p>
        <progress class="import-progress" data-import-bar max="${total}" value="0"></progress>
        <p class="log-hint">Almost there — keep this page open.</p>
      </section>`;
  }

  function renderDone() {
    const leftToMatch = allRows.length - matchedUpTo;
    const leftToAdd = resolved.some((r) => r.place && r.status !== 'added');
    if (leftToMatch || leftToAdd) saveSession();
    else clearSession(); // truly finished — nothing left to resume
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Import finished">
        <h2 class="v2-page-title">Done!</h2>
        <p>${done.added} place${done.added === 1 ? '' : 's'} added to “${escapeHtml(done.title)}”.
          ${done.duplicate ? `${done.duplicate} ${done.duplicate === 1 ? 'was' : 'were'} already on the list.` : ''}
          ${done.skipped ? `${done.skipped} couldn’t be added — they’re easy to add later by search.` : ''}</p>
        ${done.rejected?.length ? `
        <div class="import-rejected">
          <p><strong>${done.rejected.length} can’t be added as ${done.rejected.length === 1 ? 'a place' : 'places'}</strong>
            — Google answers with an area (a town or a department) rather than a spot, and a
            list holds places. Add ${done.rejected.length === 1 ? 'it' : 'them'} from the list
            page with “Add a place”, searching for something inside the area — a museum, a
            monument, the fort itself.</p>
          <ul>${done.rejected.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>
        </div>` : ''}
        ${leftToMatch || leftToAdd ? `<p class="log-hint">${leftToMatch
          ? `${leftToMatch} place${leftToMatch === 1 ? '' : 's'} in this file still to match.`
          : 'Some matched places weren’t added yet.'}</p>
        <button type="button" class="v2-btn-leather" data-import-continue>Continue with this list</button>` : ''}
        <button type="button" class="${leftToMatch || leftToAdd ? 'v2-btn-more' : 'v2-btn-leather'}" data-import-open>Open the list</button>
        <button type="button" class="v2-btn-more" data-import-again>Import another list</button>
      </section>`;
  }

  function renderSignedOut() {
    el.innerHTML = `${back}
      <div class="v2-empty">Sign in (top right) to import your lists.</div>`;
  }

  async function onFile(file) {
    fileName = file.name || '';
    let text;
    try {
      text = await file.text();
    } catch {
      renderIntro('Couldn’t read that file — try picking it again.');
      return;
    }
    allRows = takeoutRows(text);
    if (!allRows.length) {
      allRows = [];
      renderIntro('That doesn’t look like a Google Maps list export — pick one of the CSV files inside Takeout/Saved.');
      return;
    }
    matchedUpTo = 0;
    resolved = [];
    checked = new Set();
    // A big file gets a choice, not an auto-started marathon (2026-08-04
    // on-device round 2: "Matching 9 of 1080" read as parsing everything)
    if (allRows.length > IMPORT_BATCH) renderBatchPrompt();
    else await matchNextBatch();
  }

  /**
   * Rows you already have (2026-08-04 on-device feedback): a resolved
   * place whose location is in your journal or any of your lists imports
   * as an unchecked "already" row instead of a pre-checked match. Pure
   * DB lookups on google_place_id — no Google calls, so a failure here
   * quietly leaves rows as plain matches.
   */
  async function markAlreadySaved(batch) {
    const withPlace = batch.filter((r) => r.place);
    if (!withPlace.length) return;
    try {
      const placeIds = [...new Set(withPlace.map((r) => r.place.placeId))];
      const { data: locs } = await gateway.rawQuery(gateway.TABLES.LOCATIONS, {
        select: 'id,google_place_id',
        filters: { google_place_id: `in.(${placeIds.join(',')})` },
        limit: placeIds.length,
      });
      const byPlace = new Map((locs || []).map((l) => [l.google_place_id, l.id]));
      const locIds = [...byPlace.values()];
      if (!locIds.length) return;
      const me = store.get().user;
      const myLists = await fetchMyLists(store).catch(() => []);
      const listIds = myLists.map((l) => l.id);
      const [mine, entries] = await Promise.all([
        gateway.rawQuery(gateway.TABLES.YOINKS, {
          select: 'id,location_id',
          filters: { user_id: `eq.${me.id}`, location_id: `in.(${locIds.join(',')})` },
          limit: locIds.length,
        }),
        listIds.length
          ? gateway.rawQuery(gateway.TABLES.LIST_ENTRIES, {
            select: 'id,location_id',
            filters: { list_id: `in.(${listIds.join(',')})`, location_id: `in.(${locIds.join(',')})` },
            limit: 1000,
          })
          : Promise.resolve({ data: [] }),
      ]);
      const have = new Set(
        [...(mine.data || []), ...(entries.data || [])].map((r) => r.location_id)
      );
      for (const row of withPlace) {
        const locId = byPlace.get(row.place.placeId);
        if (locId && have.has(locId)) row.status = 'already';
      }
    } catch { /* a nicety, not a gate */ }
  }

  async function matchNextBatch() {
    const start = matchedUpTo;
    const batchRows = allRows.slice(start, start + IMPORT_BATCH);
    stopAsked = false;
    renderMatching(start, batchRows.length);
    const batch = await resolveTakeoutRows(batchRows, {
      shouldStop: () => destroyed || stopAsked,
      onRow: (row, i, total) => {
        const line = el.querySelector('[data-match-line]');
        const bar = el.querySelector('[data-match-bar]');
        if (line) {
          line.textContent = i + 1 < total
            ? `${i + 2} of ${total} in this batch… (${row.title || row.place?.mainText || ''} ${row.place ? '✓' : '·'})`
            : `Matched all ${total} in this batch.`;
        }
        if (bar) bar.value = i + 1;
      },
    });
    if (destroyed) return;
    matchedUpTo = start + batch.length; // a Stop mid-batch resumes exactly here
    await markAlreadySaved(batch);
    if (destroyed) return;
    const offset = resolved.length;
    resolved = resolved.concat(batch);
    batch.forEach((row, j) => {
      if (row.status === 'matched') checked.add(offset + j);
    });
    if (offset === 0) {
      // A "Want to go.csv" pours into the system list by default; anything
      // else becomes a new list named after the file. Existing custom lists
      // are offered as merge targets.
      dest = /want.to.go/i.test(fileName) ? 'wtg' : 'new';
      try {
        myCustomLists = (await fetchMyLists(store)).filter((l) => l.kind !== WANT_TO_GO.kind);
      } catch {
        myCustomLists = [];
      }
      existingListId = myCustomLists[0]?.id || null;
    }
    if (destroyed) return;
    saveSession();
    renderReview();
  }

  async function runImport() {
    const picks = resolved
      .map((row, i) => ({ row, i }))
      .filter(({ row, i }) => checked.has(i) && row.place);
    if (!picks.length) return;
    const titleInput = el.querySelector('[data-import-title]')?.value.trim();
    renderImporting(picks.length);

    let listId;
    let slug;
    let title;
    try {
      if (dest === 'wtg') {
        listId = await ensureWantToGo(store);
        slug = WANT_TO_GO.slug;
        title = WANT_TO_GO.title;
      } else if (dest === 'existing') {
        const target = myCustomLists.find((l) => l.id === existingListId);
        if (!target) throw new Error('Pick a list to merge into.');
        listId = target.id;
        slug = target.slug;
        title = target.title;
      } else {
        const list = await createList(store, { title: titleInput || defaultTitle(), visibility: 'private' });
        if (!list) throw new Error('Sign in to import lists.');
        listId = list.id;
        slug = list.slug;
        title = list.title;
      }
    } catch (err) {
      showToast(err?.message || 'Couldn’t set up the list — try again.');
      renderReview();
      return;
    }

    let added = 0;
    let skipped = 0;
    let duplicate = 0; // already in this list — the insert is a no-op
    const rejected = []; // areas Google will not let us store as a place
    const addedIds = [];
    for (let n = 0; n < picks.length; n++) {
      if (destroyed) return;
      const { row, i } = picks[n];
      try {
        const result = await createLocationFromPlace(row.place);
        if (result.rejected) {
          // An area (a locality, a department): createLocationFromPlace
          // refuses those by design, and retrying can never help. Name it on
          // the done screen and mark the row spent, rather than counting it
          // into an anonymous "skipped" and leaving it tickable forever.
          rejected.push(row.place.mainText || row.title);
          row.status = 'rejected';
          checked.delete(i);
        } else {
          const inserted = await addToList(listId, result.location.id, row.note || null);
          if (inserted) {
            addedIds.push(result.location.id);
            added++;
          } else {
            duplicate++;
          }
          // Sunk to the bottom of the review, unticked and disabled — a
          // "Continue with this list" pass can't double-add it
          row.status = 'added';
          checked.delete(i);
        }
      } catch {
        skipped++;
      }
      const line = el.querySelector('[data-import-line]');
      const bar = el.querySelector('[data-import-bar]');
      if (line) line.textContent = n + 1 < picks.length ? `Adding ${n + 2} of ${picks.length}…` : 'Wrapping up…';
      if (bar) bar.value = n + 1;
      if (n + 1 < picks.length && PACING.delayMs) await sleep(PACING.delayMs);
    }
    if (dest === 'wtg' && addedIds.length) {
      store.set({ savedLocationIds: [...new Set([...(store.get().savedLocationIds || []), ...addedIds])] });
    }
    done = { added, skipped, duplicate, rejected, slug, title };
    if (!destroyed) renderDone();
  }

  function onChange(event) {
    const file = event.target.closest('[data-import-file]');
    if (file && file.files?.[0]) {
      onFile(file.files[0]);
      return;
    }
    const check = event.target.closest('[data-import-check]');
    if (check) {
      const i = Number(check.dataset.importCheck);
      if (check.checked) checked.add(i);
      else checked.delete(i);
      // Keep the button's count honest without a re-render
      const go = el.querySelector('[data-import-go]');
      if (go) {
        go.disabled = !checked.size;
        go.textContent = `Add ${checked.size} place${checked.size === 1 ? '' : 's'}`;
      }
      const bar = el.querySelector('[data-import-bulk-bar]');
      if (bar) bar.outerHTML = bulkBarHtml() || '<div data-import-bulk-bar hidden></div>';
      saveSession();
      return;
    }
    const destRadio = event.target.closest('[name="import-dest"]');
    if (destRadio) {
      dest = destRadio.value;
      return;
    }
    const existingSelect = event.target.closest('[data-import-existing]');
    if (existingSelect) {
      // Choosing from the dropdown IS choosing the destination
      existingListId = existingSelect.value;
      dest = 'existing';
      const radio = el.querySelector('[name="import-dest"][value="existing"]');
      if (radio) radio.checked = true;
    }
  }

  function onClick(event) {
    const bulk = event.target.closest('[data-import-bulk]');
    if (bulk) {
      applyBulk(bulk.dataset.importBulk);
      return;
    }
    if (event.target.closest('[data-import-stop]')) {
      stopAsked = true;
      return;
    }
    if (event.target.closest('[data-import-more]')) {
      matchNextBatch();
      return;
    }
    if (event.target.closest('[data-import-continue]')) {
      done = null;
      renderReview();
      return;
    }
    if (event.target.closest('[data-import-go]')) {
      runImport();
      return;
    }
    if (event.target.closest('[data-import-open]')) {
      const username = store.get().profile?.username;
      if (username && done) router.navigate('list', { username, slug: done.slug });
      return;
    }
    if (event.target.closest('[data-import-again]')) {
      clearSession();
      allRows = [];
      matchedUpTo = 0;
      resolved = [];
      checked = new Set();
      done = null;
      renderIntro();
      return;
    }
    if (event.target.closest('[data-import-resume]')) {
      fetchMyLists(store)
        .then((lists) => {
          myCustomLists = lists.filter((l) => l.kind !== WANT_TO_GO.kind);
          if (!existingListId) existingListId = myCustomLists[0]?.id || null;
        })
        .catch(() => { myCustomLists = []; })
        .finally(() => { if (!destroyed) renderReview(); });
      return;
    }
    if (event.target.closest('[data-import-startover]')) {
      clearSession();
      allRows = [];
      matchedUpTo = 0;
      resolved = [];
      checked = new Set();
      renderIntro();
      return;
    }
    const backLink = event.target.closest('[data-back-lists]');
    if (backLink) {
      event.preventDefault();
      router.navigate('lists');
    }
  }

  function start() {
    if (!store.get().user) {
      if (!store.get().authReady) {
        const unsub = store.subscribe((s) => {
          if (!s.authReady || destroyed) return;
          unsub();
          start();
        });
        el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
        return;
      }
      renderSignedOut();
      return;
    }
    if (restoreSession()) {
      renderResume();
      return;
    }
    renderIntro();
  }

  el.addEventListener('change', onChange);
  el.addEventListener('click', onClick);
  // Ticks and the destination radio only persist at natural save points —
  // stringifying 1,000 rows per tap would jank an iPad — so catch the
  // leave itself: SPA navigation lands in cleanup below, a tab close here.
  const onPageHide = () => saveSession();
  window.addEventListener('pagehide', onPageHide);
  start();

  return () => {
    destroyed = true;
    saveSession();
    window.removeEventListener('pagehide', onPageHide);
    el.removeEventListener('change', onChange);
    el.removeEventListener('click', onClick);
  };
}
